import {isUndefined} from 'lodash';
import React, {Fragment, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/components/trans';
import {useInitialEffect} from '../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {
  KnowledgeBaseAccessTypesEnum,
  KnowledgeBaseModel,
  KnowledgeBaseStatusesEnum,
} from '../../../../libs/shared/core/src/models/api/knowledgeBaseModel';
import {getAccessibleKnowledgeBases} from '../../../../libs/shared/core/src/store/data/knowledgeBases/knowledgeBasesSelectors';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownCategory} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {BaseFilterDropdownProps} from '../../../components/common/filters/common/types';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {updateFiltersAction} from '../../../store/panels/analytics/filters/analyticsPanelFiltersActions';
import {useAnalyticsFilter} from '../../helpers/hooks/useAnalyticsFilter';
import {AnalyticsFilterButtonValue} from './common/analyticsFilterButtonValue';
import {analyticsFiltersConfig} from './common/analyticsFiltersConfig';
import {renderFilterTitle, useFiltersTracking} from './common/helpers';

/*
 * Constants.
 */

const {name, iconName, activeIconName} = analyticsFiltersConfig[AnalyticsFilterTypesEnum.KNOWLEDGE_BASES];

/*
 * Styles.
 */

const StyledDropdown = styled(Dropdown)`
  width: 240px;
  padding: 8px 0;
  overflow: auto;
`;

const StyledDropdownList = styled(DropdownList)`
  padding: 0;
  overflow: visible;
`;

const StyledKnowledgeBaseDropdownItem = styled(DropdownItem)`
  align-items: center;
`;

const StyledKnowledgeBaseDropdownItemContent = styled(DropdownItemContent)`
  grid-area: content;
`;

const StyledIcon = styled(Icon).attrs((p) => ({color: p.theme.greys.shade70}))`
  grid-area: left-icon;
  margin-right: 12px;
`;

/*
 * Hook.
 */

export const useAnalyticsKnowledgeBaseFilter = () => {
  const [selectedKnowledgeBaseIds] = useAnalyticsFilter(AnalyticsFilterTypesEnum.KNOWLEDGE_BASES);
  const dispatch = useAppDispatch();
  const {trackOnEdit} = useFiltersTracking(AnalyticsFilterTypesEnum.KNOWLEDGE_BASES);
  const isActive = !isUndefined(selectedKnowledgeBaseIds) && selectedKnowledgeBaseIds?.length > 0;

  // only one knowledge base selected at a time
  const selectedKnowledgeBaseId = selectedKnowledgeBaseIds?.[0];
  const allPublishedKnowledgeBases = useAppSelector(getAccessibleKnowledgeBases).filter((kb) =>
    Object.values(kb.statuses).includes(KnowledgeBaseStatusesEnum.PUBLISHED),
  );
  const externalKnowledgeBases = allPublishedKnowledgeBases.filter(
    (kb) => kb.type === KnowledgeBaseAccessTypesEnum.EXTERNAL,
  );
  const internalKnowledgeBases = allPublishedKnowledgeBases.filter(
    (kb) => kb.type === KnowledgeBaseAccessTypesEnum.INTERNAL,
  );
  const isDisabled = !Object.keys(allPublishedKnowledgeBases).length;

  const selectedKnowledgeBase = allPublishedKnowledgeBases.find((kb) => kb.id === selectedKnowledgeBaseId);
  const publishedLocales = useMemo(
    () =>
      selectedKnowledgeBase?.locales.filter(
        (locale) => selectedKnowledgeBase?.statuses[locale] === KnowledgeBaseStatusesEnum.PUBLISHED,
      ),
    [selectedKnowledgeBase],
  );

  // set KB & locale simultaneously
  const selectKnowledgeBase = (knowledgeBaseId: number) => {
    const newKnowledgeBase = allPublishedKnowledgeBases.find((kb) => kb.id === knowledgeBaseId);
    const initialLocale = newKnowledgeBase?.locales.filter(
      (locale) => newKnowledgeBase?.statuses[locale] === KnowledgeBaseStatusesEnum.PUBLISHED,
    )?.[0];

    dispatch(
      updateFiltersAction({
        [AnalyticsFilterTypesEnum.LOCALE]: initialLocale ? [initialLocale] : [],
        [AnalyticsFilterTypesEnum.KNOWLEDGE_BASES]: [knowledgeBaseId],
      }),
    );
    trackOnEdit([knowledgeBaseId], selectedKnowledgeBaseIds);
  };

  // select knowledge base for the first time
  useInitialEffect(() => {
    if (!selectedKnowledgeBaseIds?.length && allPublishedKnowledgeBases[0]) {
      selectKnowledgeBase(allPublishedKnowledgeBases[0].id);
    }
  });

  const knowledgeBaseMap: Record<number, KnowledgeBaseModel> = allPublishedKnowledgeBases.reduce(
    (acc, knowledgeBase) => ({...acc, [knowledgeBase.id]: knowledgeBase}),
    {},
  );

  const renderButton = () => (
    <AnalyticsFilterButtonValue
      selectedIds={selectedKnowledgeBaseIds}
      itemsMap={knowledgeBaseMap}
      renderItem={renderButtonItem}
      iconName={isActive ? activeIconName : iconName}
      label={
        isDisabled ? (
          <Trans id="analytics-filter-no-live-knowledge-bases">No live knowledge bases</Trans>
        ) : (
          name
        )
      }
      isActive={isActive}
      shouldLabelSelection={false}
    />
  );

  return {
    id: 'knowledge-bases-filter',
    type: AnalyticsFilterTypesEnum.KNOWLEDGE_BASES,
    isActive,
    isDisabled,
    title: renderFilterTitle(name, iconName),
    renderDropdown: (dropdownProps: BaseFilterDropdownProps) =>
      renderKnowledgeBaseDropdown(
        dropdownProps,
        externalKnowledgeBases,
        internalKnowledgeBases,
        selectKnowledgeBase,
        selectedKnowledgeBaseId,
      ),
    renderButton,

    // hook orchestration
    selectedKnowledgeBase,
    publishedLocales,
  };
};

/*
 * Helpers.
 */

function renderButtonItem(knowledgeBase: KnowledgeBaseModel) {
  return <Fragment key={knowledgeBase.id}>{knowledgeBase.names[knowledgeBase.defaultLocale]}</Fragment>;
}

function renderKnowledgeBaseDropdown(
  dropdownProps: BaseFilterDropdownProps,
  externalKnowledgeBases: ReadonlyArray<KnowledgeBaseModel> = [],
  internalKnowledgeBases: ReadonlyArray<KnowledgeBaseModel> = [],
  onSelect: (arg: number) => void,
  selectedKnowledgeBaseId?: number,
) {
  const handleOnSelect = (knowledgeBaseId: number) => {
    onSelect(knowledgeBaseId);
    dropdownProps.onRequestClose?.();
  };

  return (
    <StyledDropdown {...dropdownProps}>
      {Boolean(internalKnowledgeBases.length) && (
        <>
          <DropdownCategory>
            <Trans id="analytics-filter-internal-knowledge-base">Internal</Trans>
          </DropdownCategory>
          <StyledDropdownList>
            {internalKnowledgeBases.map((knowledgeBase) =>
              renderKnowledgeBaseDropdownItem(knowledgeBase, handleOnSelect, selectedKnowledgeBaseId),
            )}
          </StyledDropdownList>
        </>
      )}
      {Boolean(externalKnowledgeBases.length) && (
        <>
          <DropdownCategory>
            <Trans id="analytics-filter-external-knowledge-base">External</Trans>
          </DropdownCategory>
          <StyledDropdownList>
            {externalKnowledgeBases.map((knowledgeBase) =>
              renderKnowledgeBaseDropdownItem(knowledgeBase, handleOnSelect, selectedKnowledgeBaseId),
            )}
          </StyledDropdownList>
        </>
      )}
    </StyledDropdown>
  );
}

function renderKnowledgeBaseDropdownItem(
  knowledgeBase: KnowledgeBaseModel,
  onSelect: (arg: number) => void,
  selectedKnowledgeBaseId?: number,
) {
  const knowledgeBaseName = knowledgeBase.names[knowledgeBase.defaultLocale];
  const isChecked = knowledgeBase.id === selectedKnowledgeBaseId;

  return (
    <StyledKnowledgeBaseDropdownItem
      key={knowledgeBase.id}
      onSelect={() => onSelect(knowledgeBase.id)}
      isChecked={isChecked}
    >
      <StyledIcon name="knowledgeBaseFilledSmall" />
      <StyledKnowledgeBaseDropdownItemContent isChecked={isChecked}>
        <TooltipCoordinator render={() => knowledgeBaseName}>
          <TooltipOverflowCondition>{knowledgeBaseName}</TooltipOverflowCondition>
        </TooltipCoordinator>
      </StyledKnowledgeBaseDropdownItemContent>
    </StyledKnowledgeBaseDropdownItem>
  );
}
